import { useFormik } from "formik";
import React, { useEffect, useCallback, useMemo, memo } from "react";

import { CustomSelect, Error, Input, Label, Switch } from "../../Components";
import { useBadlaType } from "../../Hooks";
import { badlaTypeOptions } from "../../Static/data";
import { badlaInstrumentCreateSchema } from "../../Schema/BadlaSchema";
import { postAxios } from "../../Services/commonService";
import {
  API_BADLA_INSTRUMENT_CREATE,
  API_BADLA_INSTRUMENT_UPDATE,
} from "../../utils/api/ApiConstant";

const InstrumentCreate = ({ rowData, handleShowHide }) => {
  // Fetch badla list data
  const { badlaModifyOptions, badlaListData } = useBadlaType();

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: {
      settingName: rowData?.settingName || "",
      Duty: rowData?.settingValue?.[0]?.Duty || 15,
      displayName: rowData?.displayName || "",
      badlaType: null,
      MCX_Exchnage: null,
      COMEX_Exchnage: null,
      DGCX_Exchange: null,
      NEXT_DGCX_Exchange: null,
      MCX_Exchnage_detail: null,
      COMEX_Exchnage_detail: null,
      DGCX_Exchange_detail: null,
      NEXT_DGCX_Exchange_detail: null,
      equation: rowData?.equation || "",
      displayOrder: rowData?.displayOrder || 0,
      reverse: rowData?.reverse ?? "0",
    },
    validationSchema: badlaInstrumentCreateSchema,
    onSubmit: (values) => {
      handleUrlPayload({ values });
    },
  });

  // Ensure badlaType is preselected on mount
  useEffect(() => {
    if (badlaTypeOptions) {
      if (rowData && rowData?._id) {
        const selectedType = badlaTypeOptions.find(
          (x) => x.value === rowData?.badlaType
        );
        setFieldValue("badlaType", selectedType);
      } else {
        setFieldValue("badlaType", badlaTypeOptions[0]);
      }
    }
  }, [badlaTypeOptions, rowData]);

  useEffect(() => {
    if (badlaModifyOptions) {
      if (rowData && rowData?._id) {
        // MCX
        const selectedMCX = badlaModifyOptions.find(
          (a) => a.value === rowData?.instrumentsDetail?.[0]?.instrument_token
        );
        setFieldValue("MCX_Exchnage", selectedMCX);

        // COMEX
        const selectedCOMEX = badlaModifyOptions.find(
          (a) => a.value === rowData?.instrumentsDetail?.[1]?.instrument_token
        );
        setFieldValue("COMEX_Exchnage", selectedCOMEX);

        // DGCX
        const selectedDGCX = badlaModifyOptions.find(
          (a) => a.value === rowData?.instrumentsDetail?.[2]?.instrument_token
        );
        setFieldValue("DGCX_Exchange", selectedDGCX);

        // const selectedNextDGCX = badlaModifyOptions.find(
        //   (a) => a.value === rowData?.instrumentsDetail?.[3]?.instrument_token
        // );
        // setFieldValue("NEXT_DGCX_Exchange", selectedNextDGCX);
      } else {
        setFieldValue("MCX_Exchnage", badlaModifyOptions[0]);
        setFieldValue("COMEX_Exchnage", badlaModifyOptions[0]);
        setFieldValue("DGCX_Exchange", badlaModifyOptions[0]);
        // setFieldValue("NEXT_DGCX_Exchange", badlaModifyOptions[0]);
      }
    }
  }, [badlaModifyOptions, rowData]);

  useEffect(() => {
    if (values?.MCX_Exchnage) {
      const findBadlaType = badlaListData?.find(
        (x) => x?.instrument_token === values?.MCX_Exchnage.value
      );
      setFieldValue("MCX_Exchnage_detail", findBadlaType);
    }
  }, [values?.MCX_Exchnage, values?.COMEX_Exchnage, values?.DGCX_Exchange]);

  useEffect(() => {
    if (values?.COMEX_Exchnage) {
      const findBadlaType = badlaListData?.find(
        (x) => x?.instrument_token === values?.COMEX_Exchnage.value
      );
      setFieldValue("COMEX_Exchnage_detail", findBadlaType);
    }
  }, [values?.COMEX_Exchnage]);

  useEffect(() => {
    if (values?.DGCX_Exchange) {
      const findBadlaType = badlaListData?.find(
        (x) => x?.instrument_token === values?.DGCX_Exchange.value
      );
      setFieldValue("DGCX_Exchange_detail", findBadlaType);
    }
  }, [values?.DGCX_Exchange]);

  useEffect(() => {
    if (values?.NEXT_DGCX_Exchange) {
      const findBadlaType = badlaListData?.find(
        (x) => x?.instrument_token === values?.NEXT_DGCX_Exchange.value
      );
      setFieldValue("NEXT_DGCX_Exchange_detail", findBadlaType);
    }
  }, [values?.NEXT_DGCX_Exchange]);

  // Memoized handler to update Formik state
  const handleSelectChange = useCallback(
    (fieldName) => (selectedOption) => {
      setFieldValue(fieldName, selectedOption);
    },
    [setFieldValue]
  );

  const handleUrlPayload = useCallback(({ values }) => {
    let payload;
    let url;

    const settingValue = [{ Duty: values.Duty }];

    // Build exchangeName array by including only non-null/undefined values
    const exchangeCandidates = [
      values.MCX_Exchnage?.value,
      values.COMEX_Exchnage?.value,
      values.DGCX_Exchange?.value,
      values?.NEXT_DGCX_Exchange?.value,
    ];
    const exchangeName = exchangeCandidates.filter(
      (v) => v !== null && v !== undefined
    );

    if (rowData && rowData?._id) {
      url = API_BADLA_INSTRUMENT_UPDATE;

      // Build instrumentsDetail array by including only non-null/undefined entries
      const instrumentsCandidates = [
        values.MCX_Exchnage_detail,
        values.COMEX_Exchnage_detail,
        values.DGCX_Exchange_detail,
        values?.NEXT_DGCX_Exchange_detail,
      ];
      let instrumentsDetail = instrumentsCandidates.filter(
        (v) => v !== null && v !== undefined
      );

      payload = {
        ...rowData,
        settingName: values.settingName,
        instrumentsDetail,
        instrumentToken: exchangeName,
        settingValue,
        equation: values.equation,
        badlaType: values.badlaType.value,
        reverse: values.reverse,
        displayName: values.displayName,
      };
    } else {
      url = API_BADLA_INSTRUMENT_CREATE;
      payload = {
        settingName: values.settingName,
        settingValue,
        badlaType: values.badlaType.value,
        exchangeName,
        equation: values.equation,
        reverse: values.reverse,
        displayName: values.displayName,
      };
    }

    handleFormSubmit({ url, payload });
  }, []);

  const handleFormSubmit = useCallback(async ({ url, payload }) => {
    const res = await postAxios({ url, payload });
    if (res) handleShowHide();
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <div className="row m-0 p-0">
        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="settingName" className="form-label" isRequired={true}>
            Badla Name
          </Label>
          <Input
            type="text"
            className="form-control"
            name="settingName"
            id="settingName"
            placeholder="Enter exchange name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.settingName}
            autoFocus={true}
          />
          {errors.settingName && touched.settingName && (
            <Error>{errors.settingName}</Error>
          )}
        </div>

        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="displayName" className="form-label" isRequired={true}>
            Display Name
          </Label>
          <Input
            type="text"
            className="form-control"
            name="displayName"
            id="displayName"
            placeholder="Enter display name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.displayName}
          />
          {errors.displayName && touched.displayName && (
            <Error>{errors.displayName}</Error>
          )}
        </div>

        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="Duty" className="form-label" isRequired={true}>
            Duty
          </Label>
          <Input
            type="number"
            className="form-control"
            name="Duty"
            id="Duty"
            placeholder="Enter Duty"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.Duty}
          />
          {errors.Duty && touched.Duty && <Error>{errors.Duty}</Error>}
        </div>

        {/* Reusable Select Fields */}
        <SelectField
          label="Badla Type"
          name="badlaType"
          options={badlaTypeOptions}
          value={values.badlaType}
          onChange={handleSelectChange("badlaType")}
          error={errors.badlaType}
          touched={touched.badlaType}
        />

        <SelectField
          label="Select MCX Exchange"
          name="MCX_Exchnage"
          options={badlaModifyOptions}
          value={values.MCX_Exchnage}
          onChange={handleSelectChange("MCX_Exchnage")}
          error={errors.MCX_Exchnage}
          touched={touched.MCX_Exchnage}
        />

        <SelectField
          label="Select COMEX Exchange"
          name="COMEX_Exchnage"
          options={badlaModifyOptions}
          value={values.COMEX_Exchnage}
          onChange={handleSelectChange("COMEX_Exchnage")}
          error={errors.COMEX_Exchnage}
          touched={touched.COMEX_Exchnage}
        />

        <SelectField
          label="Select DGCX Exchange"
          name="DGCX_Exchange"
          options={badlaModifyOptions}
          value={values.DGCX_Exchange}
          onChange={handleSelectChange("DGCX_Exchange")}
          error={errors.DGCX_Exchange}
          touched={touched.DGCX_Exchange}
        />

        <SelectField
          label="Select Next DGCX Exchange"
          name="NEXT_DGCX_Exchange"
          options={badlaModifyOptions}
          value={values.NEXT_DGCX_Exchange}
          onChange={handleSelectChange("NEXT_DGCX_Exchange")}
          error={errors.NEXT_DGCX_Exchange}
          touched={touched.NEXT_DGCX_Exchange}
        />

        <div className="col-md-6 m-0 p-1">
          <Label htmlFor="equation" className="form-label" isRequired={true}>
            Add equation
          </Label>
          <Input
            type="text"
            className="form-control"
            name="equation"
            id="equation"
            placeholder="Enter equation"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.equation}
          />
          {errors.equation && touched.equation && (
            <Error>{errors.equation}</Error>
          )}
          {values.equation && <span>{values.equation}</span>}
        </div>

        <div className="col-md-6 m-0 p-1 d-flex justify-content-start align-items-center">
          <Switch
            className="form-control"
            name="equation"
            getValue={!(values.reverse === "0")}
            onChange={() => {
              setFieldValue("reverse", values.reverse === "0" ? "1" : "0");
            }}
          />
          <Label
            htmlFor="equation"
            className="form-label ml-3"
            isRequired={false}
          >
            Reverse equation
          </Label>
        </div>
      </div>
      <button type="submit" id="form-submit-btn" hidden={true}>
        Submit
      </button>
    </form>
  );
};

// Memoized Reusable Select Field Component
const SelectField = memo(
  ({ label, name, options, value, onChange, error, touched }) => {
    return (
      <div className="col-md-6 m-0 p-1">
        <Label htmlFor={name} className="form-label" isRequired={true}>
          {label}
        </Label>
        <CustomSelect options={options} value={value} onChange={onChange} />
        {error && touched && <Error>{error}</Error>}
      </div>
    );
  }
);

export default InstrumentCreate;
